/* eslint-disable */
import PoTableMultiple from '@/Components/POTableMultiple/poTable';
import { useMultiplePo } from './contexts/MultiplePoContext';
import Functional from './functional';
import { useMultiPoApi } from './hooks/useMultiPOApi';
import MultiPOGeneralDetails from './MultiPOGeneralDetails';
import {
	CalendarDays,
	Edit,
	UserCheck,
	UserPlus,
	UserRoundX,
	Users,
	X,
} from 'lucide-react';
import { useEffect, useMemo } from 'react';
import { SelectPicker } from 'rsuite';
import {
	getUnitConvertedUnit,
	getHigherUnitAndQuantity,
	convertToLowerUnit,
} from '@/Helpers';
import { useTableCellRenderer } from './hooks/useTableCellRender';
import { getTableRenderer } from './config/tableRenderRowData';
import { calculateRowVariance, findProductFromList, getLCM, calculateRepeatDateSelections, isNotMultipleOf } from './utils/utils';
import Legend from './Legend';
import productionPlanServices from '@/Services/Analytics Services/production-plan.services';
import moment from 'moment';
import { useDispatch } from 'react-redux';
import { modalActions } from '@/Actions';
import { useState, useRef } from 'react';
import { Button } from '@mui/material';
import { Check, AlertTriangle, Info } from 'lucide-react';
import { notificationServices } from '@/Services';
import { countToKg, countUnitLabel, getRowItemId, isCountItem, minWeightText } from './utils/countUnits';
import { isRowWisePlanSheet, parseClipboardGrid, pivotRowWisePlan } from './utils/rowWisePlanPaste';

const SHIFT_COLORS = ['#3B82F6', '#F59E0B', '#8B5CF6', '#10B981', '#EF4444'];

// Sync validity between body and footer
const modalSync = {
	onUpdate: null,
	emit(isValid) {
		if (this.onUpdate) this.onUpdate(isValid);
	},
};

const ShiftDistributionContent = ({
	shifts = [],
	distributionRef,
	onValidChange,
}) => {
	const validShifts = shifts.filter((s) => s.value !== -999);

	const [distribution, setDistribution] = useState(() => {
		const initial = {};
		if (validShifts.length === 0) return initial;
		const equalShare = parseFloat((100 / validShifts.length).toFixed(2));
		validShifts.forEach((shift, idx) => {
			initial[shift.value] =
				idx === 0
					? parseFloat(
						(
							100 -
							equalShare * (validShifts.length - 1)
						).toFixed(2)
					)
					: equalShare;
		});
		return initial;
	});

	const total = Object.values(distribution).reduce(
		(sum, val) => sum + (parseFloat(val) || 0),
		0
	);
	const isValid = Math.abs(total - 100) < 0.01;

	useEffect(() => {
		modalSync.emit(isValid);
	}, [isValid]);

	if (distributionRef) {
		distributionRef.current = { distribution, isValid };
	}

	const handleChange = (shiftId, value) => {
		setDistribution((prev) => ({
			...prev,
			[shiftId]:
				value === '' ? '' : parseFloat(parseFloat(value).toFixed(2)),
		}));
	};

	return (
		<div className='flex flex-col h-full'>
			<div className='space-y-1 overflow-y-auto max-h-48 flex-1'>
				{validShifts.map((shift, idx) => (
					<div
						key={shift.value}
						className='flex items-center justify-between px-3 py-1.5 bg-gray-50 rounded border border-gray-200'
					>
						<div className='flex items-center gap-3'>
							<div
								className='w-3 h-3 rounded-full'
								style={{
									backgroundColor:
										SHIFT_COLORS[idx % SHIFT_COLORS.length],
								}}
							/>
							<span className='text-base font-medium text-gray-800'>
								{shift.label}
							</span>
						</div>
						<div className='flex items-center gap-2'>
							<input
								type='number'
								value={distribution[shift.value]}
								onChange={(e) =>
									handleChange(shift.value, e.target.value)
								}
								className='w-20 px-3 py-2 text-base text-right border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500'
								min='0'
								max='100'
								step='0.01'
							/>
							<span className='text-base text-gray-500'>%</span>
						</div>
					</div>
				))}
			</div>
			<div className='flex items-center gap-3 pt-3'>
				<span className='text-sm font-medium text-gray-600'>
					Total:
				</span>
				<div className='flex-1 h-3 bg-gray-200 rounded-full overflow-hidden'>
					<div
						className={`h-full transition-all ${isValid ? 'bg-green-500' : 'bg-orange-400'
							}`}
						style={{ width: `${Math.min(total, 100)}%` }}
					/>
				</div>
				<span
					className={`text-base font-bold ${isValid ? 'text-green-600' : 'text-orange-500'
						}`}
				>
					{total.toFixed(2)}%
				</span>
				{isValid ? (
					<Check className='w-5 h-5 text-green-600' />
				) : (
					<AlertTriangle className='w-5 h-5 text-orange-500' />
				)}
			</div>
			{!isValid && (
				<div className='flex flex-col gap-0.5 mt-1'>
					<p className='text-sm text-orange-500 font-medium'>
						{total < 100
							? `You need to allocate ${(100 - total).toFixed(
								2
							)}% more`
							: `You have allocated ${(total - 100).toFixed(
								2
							)}% too much`}
					</p>
					<p className='text-xs font-bold text-red-500 uppercase tracking-tight'>
						Total should be equal to 100%
					</p>
				</div>
			)}
		</div>
	);
};

const ReactiveSaveButton = ({ distributionRef, onSave }) => {
	const [isValid, setIsValid] = useState(
		distributionRef.current?.isValid || false
	);

	useEffect(() => {
		modalSync.onUpdate = (v) => setIsValid(v);
		return () => {
			modalSync.onUpdate = null;
		};
	}, []);

	return (
		<Button
			onClick={() => {
				if (distributionRef.current?.isValid) {
					onSave(distributionRef.current?.distribution);
				}
			}}
			variant='contained'
			disabled={!isValid}
			className={`${isValid
				? 'bg-blue-600 hover:bg-blue-700'
				: 'bg-gray-300 pointer-events-none'
				} text-white px-8 transition-all duration-200`}
		>
			Save And Generate Plan
		</Button>
	);
};

function MultiPoContent(props) {
	const {
		fields,
		mode,
		setConfirmationModal,
		modeName = '',
		handleOpenProductionPlanModal = () => { },
		handelOpenModal = () => { },
	} = props;
	const {
		dispatch,
		state,
		lists,
		tableData,
		setTableData,
		setLists,
		changeQtyRef,
		editMode,
		viewMode,
		storeList,
		editingRowIndex,
		setEditingRowIndex,
		getProductData,
		skipRowReloadRef,
		productInfoById,
		qtyByWeight,
		refreshIngredients,
		workerAllocations,
		setWorkerAllocations,
		sortColumn,
		setSortColumn,
		sortType,
		setSortType,
		orderwise,
		prevTableData,
		setPrevTableData,
		productList,
		batches,
	} = useMultiplePo();
	const { } = useMultiPoApi(
		fields,
		state,
		mode?.tableApiFetch,
		editMode,
		viewMode,
		mode?.tableTriggers
	);
	const reduxDispatch = useDispatch();

	function getTableQtyInfo() {
		if (!tableData?.rows || tableData.rows.length === 0) return false;

		return tableData.rows.some((row) =>
			Object.values(row).some(
				(cell) =>
					cell?.qty !== '' &&
					cell?.qty !== null &&
					cell?.qty !== undefined
			)
		);
	}
	const handleAlertCloseConfirm = () => {
		setConfirmationModal({
			isOpen: false,
			message: '',
			heading: '',
			isAlert: false,
			alertType: 'info',
		});
	};

	const getDependentFields = (changedFieldId, value) => {
		const dependentFields = [];

		const findDependents = (fieldId, value) => {
			fields?.forEach((field) => {
				const hasDependsOnMatch =
					field?.dependsOn?.length > 0 &&
					field.dependsOn.some((dep) => {
						const [key, val] = Object.entries(dep)[0] || [];
						return (
							key === fieldId &&
							val?.toLowerCase() === value?.toLowerCase()
						);
					});
				if (field?.enableField === fieldId || hasDependsOnMatch) {
					dependentFields.push(field.id);
					findDependents(field.id, value);
				}
			});
		};

		findDependents(changedFieldId, value);
		return dependentFields;
	};

	// Change the field but keep the table: the row reload it triggers is skipped once.
	const handleKeepItemsConfirm = (fieldId, value) => {
		handleAlertCloseConfirm();
		skipRowReloadRef.current = true;
		const payload = { [fieldId]: value };
		getDependentFields(fieldId, value).forEach((depFieldId) => {
			payload[depFieldId] = null;
		});
		dispatch({ type: 'SET_STATE', payload });
	};

	const handleAlertConfirmSave = (fieldId, value) => {
		setConfirmationModal({
			isOpen: false,
			message: '',
			heading: '',
			isAlert: false,
			alertType: 'info',
		});

		setTableData((prev) => {
			return {
				...prev,
				rows: [],
			};
		});

		const payload = { [fieldId]: value };
		const dependentFields = getDependentFields(fieldId, value);
		dependentFields.forEach((depFieldId) => {
			payload[depFieldId] = null;
		});

		dispatch({
			type: 'SET_STATE',
			payload: payload,
		});
	};



	const handleButtonClicks = async (fieldId, value) => {
		if (fieldId === 'refersh') {
			refreshIngredients(state?.kitchen, true);
			setConfirmationModal({
				isOpen: true,
				heading: 'Refresh Batch Size',
				message:
					'Latest Batch Sizes are being fetched. Please wait while the page refreshes with the latest data.',
				isAlert: true,
				alertType: 'info',
				showCrossBtn: true,
				handleCloseConfirmation: handleAlertCloseConfirm,
			});
			return;
		}
		if (fieldId === 'addProducts') {
			handelOpenModal();
			return;
		}
		if (fieldId === 'pasteExcel') {
			try {
				const data = await navigator.clipboard.readText();
				const rows = data.split(/\r?\n/).filter((row) => row.trim());
				if (rows.length < 2) {
					notificationServices.generateNotification({
						type: 'warning',
						message: 'No valid data found in clipboard. Please copy rows from Excel including headers.',
					});
					return;
				}

				let headers = rows[0].split('\t').map((h) => h.trim());
				let rowData = rows
					.slice(1)
					.map((row) => row.split('\t').map((cell) => cell.trim()));
				// Row-wise plan sheet (Date | Material | Planned Qty per row): pivot it into the
				// product-wise grid read below.
				let pivotWarnings = [];
				const grid = parseClipboardGrid(data);
				if (isRowWisePlanSheet(grid)) {
					const pivot = pivotRowWisePlan(grid, state);
					if (!pivot?.rows?.length) {
						notificationServices.generateNotification({
							type: 'warning',
							message: pivot?.warnings?.[0] || 'No planned quantities found for the selected plan period.',
						});
						return;
					}
					headers = pivot.headers;
					rowData = pivot.rows;
					pivotWarnings = pivot.warnings;
				}
				const isWorkerRow = (row) => {
					const label = (row[1] || '').toLowerCase().trim();
					return /^(worker|workers|grade).*[abc]$/i.test(label);
				};
				const isHolidayRow = (row) => {
					const label = (row[1] || '').toLowerCase().trim();
					return label === 'holiday';
				};
				const parsedData = rowData.filter(
					(row) => !isWorkerRow(row) && !isHolidayRow(row)
				);
				const workerRows = rowData.filter((row) => isWorkerRow(row));
				const holidayRows = rowData.filter((row) => isHolidayRow(row));

				const {
					comingData,
					prevData,
					excelWorkerAllocations,
					errors,
					warnings: matchWarnings,
				} = findProductFromList(
					parsedData,
					headers,
					tableData,
					state,
					prevTableData,
					workerRows,
					holidayRows,
					productList,
					batches
				);
				const warnings = [...pivotWarnings, ...(matchWarnings || [])];

				const executePaste = () => {
					setTableData(comingData);
					setPrevTableData(prevData);
					setWorkerAllocations(excelWorkerAllocations);
					dispatch({
						type: 'SET_STATE',
						payload: {
							currentStep: 4,
						},
					});
					notificationServices.generateNotification({
						type: 'success',
						message: 'Data pasted successfully! Moved to Summary step.',
					});
				};

				if (errors.length > 0) {
					setConfirmationModal({
						isOpen: true,
						heading: 'Validation Error',
						message: (
							<div className='flex flex-col gap-2'>
								<p className='font-medium text-red-600'>
									Batch/Pack size validation failed for following
									products:
								</p>
								<ul className='list-disc pl-5 text-sm space-y-1'>
									{errors.map((err, idx) => (
										<li key={idx}>{err}</li>
									))}
								</ul>
							</div>
						),
						isAlert: true,
						alertType: 'warning',
						showCrossBtn: true,
						handleCloseConfirmation: handleAlertCloseConfirm,
					});
					return;
				}

				if (warnings?.length > 0) {
					setConfirmationModal({
						isOpen: true,
						heading: 'Paste Warning',
						message: (
							<div className='flex flex-col gap-2'>
								<p className='font-medium text-amber-600'>
									Some items or worker data couldn't be fully matched. Do you want to proceed with the matched data?
								</p>
								<ul className='list-disc pl-5 text-sm space-y-1 max-h-40 overflow-auto'>
									{warnings.map((warn, idx) => (
										<li key={idx}>{warn}</li>
									))}
								</ul>
							</div>
						),
						isAlert: false,
						alertType: 'warning',
						showCrossBtn: true,
						handleCloseConfirmation: handleAlertCloseConfirm,
						handleConfirmSave: () => {
							executePaste();
							handleAlertCloseConfirm();
						},
					});
					return;
				}

				executePaste();

			} catch (err) {
				console.error('Paste error:', err);
				notificationServices.generateNotification({ type: 'error', message: err?.message || 'Failed to paste data' });
			}
		}

	}
	const handleHeaderValueChange = (fieldId, value) => {
		if (value === 'click') {
			handleButtonClicks(fieldId, value);
			return;
		}
		const tableRowData = getTableQtyInfo();
		const skipDiscardWarning = modeName === 'buyerOrder'
			|| mode?.silentHeaderChange === true;
		// A field that only reloads the rows (e.g. Expected Delivery Date) - not one the columns
		// depend on - can also be changed keeping the items and quantities already entered.
		const canKeepItems = mode?.tableTriggers?.row?.includes(fieldId)
			&& !mode?.tableTriggers?.header?.includes(fieldId);
		if (tableRowData && !skipDiscardWarning && canKeepItems) {
			return setConfirmationModal({
				isOpen: true,
				heading: 'Warning',
				message: 'Changing this reloads the items. Keep the items and quantities already entered, or discard them?',
				customButton: [
					{
						label: 'Cancel',
						onClick: handleAlertCloseConfirm,
						className: 'bg-gray-100 hover:bg-gray-200 text-gray-700',
					},
					{
						label: 'Keep Items',
						onClick: () => handleKeepItemsConfirm(fieldId, value),
						className: 'bg-blue-600 hover:bg-blue-700 text-white',
					},
					{
						label: 'Discard Items',
						onClick: () => handleAlertConfirmSave(fieldId, value),
						className: 'bg-amber-500 hover:bg-amber-600 text-white',
					},
				],
			});
		}
		if (tableRowData && !skipDiscardWarning) {
			return setConfirmationModal({
				isOpen: true,
				heading: 'Warning',
				message: 'If you leave, your Unsaved Changes Will be Discarded',
				isAlert: true,
				alertType: 'warning',
				handleCloseConfirmation: handleAlertCloseConfirm,
				handleConfirmSave: () => handleAlertConfirmSave(fieldId, value),
			});
		}
		const payload = { [fieldId]: value };
		const dependentFields = getDependentFields(fieldId, value);
		dependentFields.forEach((depFieldId) => {
			payload[depFieldId] = null;
		});

		dispatch({
			type: 'SET_STATE',
			payload: payload,
		});
	};

	const handleDeleteRow = (rowData) => {
		const rowIndex = tableData?.rows?.findIndex((r) => r === rowData);
		if (rowIndex === -1) return;

		const newRows = [...tableData?.rows];
		newRows.splice(rowIndex, 1);
		const updatedRows = newRows.map((row, index) => ({
			...row,
			Actions: { rowIndex: index },
		}));

		setTableData({ ...tableData, rows: updatedRows });
	};

	const getTotal = (rowData) => {
		const total = Object.values(rowData).reduce((acc, curr) => {
			if (curr?.qty) {
				return acc + Number(curr?.qty);
			}
			return acc;
		}, 0);
		const unit =
			Object.values(rowData).find(
				(cell) => cell && typeof cell === 'object' && cell.unit
			)?.unit || '';
		if (total === 0) return '';
		// Count items: their sub-unit ("pax") for "pc", and the weight when entering by weight.
		const info = productInfoById?.[getRowItemId(rowData)];
		if (isCountItem(info)) {
			const label = countUnitLabel(info, unit);
			// Both figures whenever the min weight is known - led by the one being ordered in.
			const kg = info?.minWeight ? countToKg(total, info.minWeight) : null;
			if (kg === null) return `${total} ${label}`;
			return qtyByWeight ? `${kg} kg (${total} ${label})` : `${total} ${label} (${kg} kg)`;
		}
		return `${total?.toFixed(3)} ${unit} `;
	};

	const nonTableProducts = useMemo(() => {
		const allProducts = getProductData;
		const filtered = allProducts.filter((p) => {
			return !tableData.rows.some((r) => r.SkuCode === p.clientVariantId);
		});
		return filtered;
	}, [tableData.rows, getProductData]);

	const handleProductSwap = (rowData, newProductId) => {
		const rowIndex = tableData?.rows?.findIndex((r) => r === rowData);
		if (rowIndex === -1) return;

		const allProducts = getProductData;
		const newProduct = allProducts.find((p) => p.value === newProductId);
		if (newProduct) {
			const updatedRows = [...tableData.rows];
			const oldRow = updatedRows[rowIndex];
			const newRow = {
				...oldRow,
				Actions: { rowIndex: rowIndex },
				'Item Name': newProduct.viewOnlyLabel,
				itemId: newProduct.value,
				SkuCode: newProduct.clientVariantId,
				_isManuallySelected: true,
				_isNotFound: false,
				_isNewFromDropdown: false,
				_matchType: 'none',
				...storeList.current?.reduce((acc, store) => {
					const oldStoreData = oldRow[`${store.id}`] || {};
					acc[`${store.id}`] = {
						...oldStoreData,
						unit: getUnitConvertedUnit(null, newProduct.unit, true),
						itemId: newProduct.value,
						productId: newProduct.productId,
					};
					return acc;
				}, {}),
			};

			updatedRows[rowIndex] = newRow;
			const rowsWithUpdatedIndex = updatedRows.map((row, index) => ({
				...row,
				Actions: { rowIndex: index },
			}));

			setTableData({ ...tableData, rows: rowsWithUpdatedIndex });
		}
		setEditingRowIndex(null);
	};

	const handleChange = (value, storeData, rowData, header) => {
		const rowIndex = tableData?.rows?.findIndex((r) => r === rowData);
		if (rowIndex === -1) return;

		if (
			['pc', 'count'].includes(storeData?.unit) &&
			value?.toString()?.includes('.')
		) {
			return;
		}
		// Get visible fields based on orderType (check dependsOn conditions)
		const visibleFields = fields?.filter((field) => {
			if (!field?.dependsOn || field.dependsOn.length === 0) return true;
			return field.dependsOn.some((dep) => {
				const [key, val] = Object.entries(dep)[0] || [];
				return (
					state?.[key]?.toLowerCase?.() === val?.toLowerCase?.() ||
					state?.[key] === val
				);
			});
		});

		// Find empty required fields
		const emptyFields = visibleFields?.filter((field) => {
			if (
				!field?.required ||
				field?.type === 'display' ||
				field?.type === 'tabs'
			)
				return false;
			const fieldValue = state?.[field.id];
			return (
				fieldValue === null ||
				fieldValue === undefined ||
				fieldValue === ''
			);
		});

		if (emptyFields?.length > 0 && !editMode) {
			const emptyFieldLabels = emptyFields.map((f) => f.label).join(', ');
			setConfirmationModal({
				isOpen: true,
				heading: 'Warning',
				message: `Please fill the required fields: ${emptyFieldLabels}`,
				isAlert: true,
				alertType: 'warning',
				showCrossBtn: true,
				handleCloseConfirmation: handleAlertCloseConfirm,
			});
			return;
		}
		const newRows = [...tableData?.rows];
		if (header?.id) {
			let oldQty = newRows[rowIndex][header?.id]?.qty;
			if (
				parseFloat(oldQty) !== parseFloat(value) &&
				(editMode || orderwise)
			) {
				changeQtyRef.current = false;
			}
			newRows[rowIndex][header?.id] = {
				...newRows[rowIndex][header?.id],
				qty: value,
			};
		} else {
			newRows[rowIndex] = {
				...newRows[rowIndex],
				qty: value,
			};
		}
		setTableData({
			...tableData,
			rows: newRows,
		});
	};
	const renderStoreCell = useTableCellRenderer({
		mode: mode?.tableCellKey,
		viewMode: viewMode || mode?.readOnlyTable === true,
		onChange: handleChange,
		options: lists?.storeOptions,
	});

	const isDateHoliday = (dateStr) => {
		const allocation = workerAllocations[dateStr];
		if (allocation && allocation.isHoliday !== undefined)
			return allocation.isHoliday;
		return false;
	};

	const handleCheckBoxChange = (value, rowIndex, header) => {
		const newRows = [...tableData?.rows];
		const columns = tableData?.headers;
		let row = newRows[rowIndex];
		const dateStr = header?.id?.replace('date_', '');
		const isPastDate =
			String(header?.id).startsWith('date_') &&
			moment(dateStr).isBefore(moment(), 'day');
		const isHoliday =
			String(header?.id).startsWith('date_') && isDateHoliday(dateStr);
		if (
			!row?.frequency ||
			(row?.frequency === 'repeat' && !row?.repeatDaysNumber) ||
			row?.frequency === 'daily' ||
			isPastDate ||
			isHoliday
		) {
			return;
		}
		const resetRow = Object.entries(row)?.reduce((acc, [key, item]) => {
			if (String(key).startsWith('date_')) {
				acc[key] = false;
			}
			return acc;
		}, {});
		row = {
			...row,
			...(row?.frequency === 'repeat' ? resetRow : {}),
			[header?.id]: value,
		};

		if (row?.frequency === 'repeat' && value === true) {
			const repeatDays = row?.repeatDaysNumber;
			if (repeatDays && repeatDays > 0) {
				const dateColumns = columns
					?.filter((col) => String(col?.id).startsWith('date_'))
					?.sort((a, b) => String(a.id).localeCompare(String(b.id)));

				const selectedDateIndex = dateColumns?.findIndex(
					(col) => col.id === header?.id
				);
				// Interval is repeatDays + 1 because "repeat after 2" means: check, skip 2, check
				const interval = repeatDays + 1;

				if (selectedDateIndex !== -1 && dateColumns) {
					// Go backwards from selected date
					for (let i = selectedDateIndex; i >= 0; i -= interval) {
						const dateCol = dateColumns[i];
						if (dateCol) {
							const colDateStr = dateCol.id.replace('date_', '');
							const isPastDate = moment(colDateStr).isBefore(
								moment(),
								'day'
							);
							row[dateCol.id] = !(
								isPastDate || isDateHoliday(colDateStr)
							);
						}
					}
					// Go forwards from selected date
					for (
						let i = selectedDateIndex + interval;
						i < dateColumns.length;
						i += interval
					) {
						const dateCol = dateColumns[i];
						if (dateCol) {
							const colDateStr2 = dateCol.id.replace('date_', '');
							const isPastDate = moment(colDateStr2).isBefore(
								moment(),
								'day'
							);
							row[dateCol.id] = !(
								isPastDate || isDateHoliday(colDateStr2)
							);
						}
					}
				}
			}
		}

		newRows[rowIndex] = row;
		setTableData({
			...tableData,
			rows: newRows,
		});
	};

	const handleFrequencyChange = (value, rowIndex, header) => {
		const newRows = [...tableData?.rows];
		const columns = tableData?.headers;
		let row = { ...newRows[rowIndex] };

		if (value !== newRows[rowIndex]?.frequency && value !== 'custom') {
			const dateUpdates = columns?.reduce((acc, col) => {
				if (String(col?.id).startsWith('date_')) {
					acc[col.id] = false;
				}
				return acc;
			}, {});
			row = {
				...row,
				...dateUpdates,
				...(value === 'repeat' && !newRows[rowIndex]?.repeatDaysNumber
					? { repeatDaysNumber: 2 }
					: {}),
			};
		}
		row = {
			...row,
			[header?.id]: value,
			increaseHeight: value === 'repeat' || value === 'custom',
		};
		if (value === 'daily') {
			const dateUpdates = columns?.reduce((acc, col) => {
				if (String(col?.id).startsWith('date_')) {
					const colDate = String(col.id).replace('date_', '');
					const isPastDate = moment(colDate).isBefore(
						moment(),
						'day'
					);
					acc[col.id] = !(isPastDate || isDateHoliday(colDate));
				}
				return acc;
			}, {});
			row = { ...row, ...dateUpdates };
		}
		newRows[rowIndex] = row;
		setTableData({
			...tableData,
			rows: newRows,
		});
	};

	const handleRepeatDaysChange = (name, value, rowIndex) => {
		if (String(value).includes('.')) {
			return;
		}
		const newRows = [...tableData?.rows];
		const columns = tableData?.headers;
		const row = newRows[rowIndex];
		const prevRepeatDays = row?.repeatDaysNumber;

		if (prevRepeatDays && prevRepeatDays > 0) {
			const dateColumns = columns
				?.filter((col) => String(col?.id).startsWith('date_'))
				?.sort((a, b) => String(a.id).localeCompare(String(b.id)));
			const firstSelectedIndex =
				dateColumns?.findIndex((col) => row[col.id] === true) ?? 0;
			const anchorIndex =
				firstSelectedIndex !== -1 ? firstSelectedIndex : 0;

			const dateResets =
				dateColumns?.reduce((acc, col) => {
					acc[col.id] = false;
					return acc;
				}, {}) || {};

			const dateUpdates =
				value > 0
					? calculateRepeatDateSelections(
						dateColumns,
						value,
						anchorIndex
					)
					: {};

			const filteredDateUpdates = Object.keys(dateUpdates).reduce(
				(acc, key) => {
					const keyDate = key.replace('date_', '');
					const isPastDate = moment(keyDate).isBefore(
						moment(),
						'day'
					);
					acc[key] = !(isPastDate || isDateHoliday(keyDate));
					return acc;
				},
				{}
			);

			newRows[rowIndex] = {
				...row,
				...dateResets,
				...filteredDateUpdates,
				repeatDaysNumber: value,
			};
		} else {
			newRows[rowIndex] = {
				...row,
				repeatDaysNumber: value,
			};
		}

		setTableData({
			...tableData,
			rows: newRows,
		});
	};

	const handleDateQtyChange = (value, rowIndex, header) => {
		const newRows = [...tableData?.rows];
		let row = { ...newRows[rowIndex] };
		if (
			['pc', 'count'].includes(row?.unit) &&
			value?.toString()?.includes('.')
		) {
			return;
		}
		if (header?.id) {
			const numValue = parseFloat(value);
			// if (isNaN(numValue)) {
			row[header?.id] = numValue;
			// } else {
			// 	// Enforce ceiling based on batch
			// 	const batch = parseFloat(row.batch) || 0;
			// 	row[header?.id] = batch > 0 ? Math.ceil(numValue / batch) * batch : Math.ceil(numValue);
			// }
		}
		changeQtyRef.current = false;
		if (row._totalTargetQuantity !== undefined) {
			row.variance = calculateRowVariance(
				row,
				row._totalTargetQuantity,
				row.originalUnit,
				row.batch,
				row.packSize?.quantity
			);
		}

		const today = moment().startOf('day');
		let totalProduction = 0;
		Object.entries(row).forEach(([key, value]) => {
			if (String(key).startsWith('date_')) {
				const dateStr = String(key).replace('date_', '');
				const entryDate = moment(dateStr).startOf('day');

				if (entryDate.isBefore(today)) {
					const displayFulfilled =
						parseFloat(row[`_fulfilled_${key}`]) || 0;
					totalProduction += displayFulfilled;
				} else {
					const displayPlanned =
						value === false ? 0 : parseFloat(value) || 0;
					totalProduction += displayPlanned;
				}
			}
		});

		row.finalProduction = totalProduction;
		row.planned = totalProduction;

		const batchVal = parseFloat(row.batch) || 0;
		// Pack size is ignored: validity is batch-multiple only.
		const packSizeVal = 0;
		let hasError = false;
		Object.entries(row).forEach(([key, val]) => {
			if (key.startsWith('date_') && val !== false) {
				const qty = parseFloat(val);
				const isInvalidBatch = isNotMultipleOf(qty, batchVal);
				const isInvalidPack = isNotMultipleOf(qty, packSizeVal);
				if (isInvalidBatch || isInvalidPack) hasError = true;
			}
		});
		// Error-driven height is tracked separately so it doesn't clobber the
		// frequency editor's increaseHeight (which also makes the row tall).
		row.hasQtyError = hasError;
		// Mark the edited cell as touched so the invalid-qty border can show.
		// (Previously handleBlur did this; the date input now commits via this
		// function alone, so fold it in here to avoid two setTableData calls
		// clobbering each other with stale state.)
		if (header?.id) {
			row[`_touched_${header.id}`] = true;
		}

		newRows[rowIndex] = row;
		setTableData({
			...tableData,
			rows: newRows,
		});
	};

	const handleVarianceAdjust = (rowIndex) => {
		const newRows = [...tableData?.rows];
		let row = { ...newRows[rowIndex] };
		let variance = parseFloat(row.variance) || 0;
		if (variance === 0) return;

		const originalUnit = row.originalUnit;
		const batch = parseFloat(row.batch) || 0;
		// const packSize = parseFloat(row.packSize?.quantity) || 0;
		const packSize = 1;
		const totalTarget = row._totalTargetQuantity || 0;

		const constraintUnit = (batch > 0 && packSize > 0) ? getLCM(batch, packSize) : (batch || packSize || 1);

		const dateColumns = tableData.headers
			?.filter((col) => String(col.id).startsWith('date_'))
			?.sort((a, b) => a.id.localeCompare(b.id));

		const today = moment().startOf('day');
		const remainingDateKeys = dateColumns
			?.filter((col) => {
				const date = moment(col.id.replace('date_', '')).startOf('day');
				return date.isSameOrAfter(today) && row[col.id] !== false;
			})
			?.map((col) => col.id);

		if (remainingDateKeys.length === 0) return;

		// Calculate total units (multiples of constraintUnit) to adjust
		let unitsInt = Math.ceil(Math.abs(variance) / constraintUnit);

		if (variance < 0) {
			// -ve variance (Shortage): ADD production.
			const zeroQtyKeys = remainingDateKeys.filter(
				(key) => !row[key] || parseFloat(row[key]) === 0
			);
			const targetKeys =
				zeroQtyKeys.length > 0 ? zeroQtyKeys : remainingDateKeys;

			const unitsPerDay = Math.floor(unitsInt / targetKeys.length);
			let extraUnits = unitsInt % targetKeys.length;

			targetKeys.forEach((key, index) => {
				const dayAdjustmentUnits =
					unitsPerDay + (index < extraUnits ? 1 : 0);
				if (dayAdjustmentUnits > 0) {
					const currentDisplayQty = parseFloat(row[key]) || 0;
					const adjustment = dayAdjustmentUnits * constraintUnit;
					const targetQty = currentDisplayQty + adjustment;

					// Align to constraintUnit
					row[key] = Math.ceil(targetQty / constraintUnit) * constraintUnit;
					row[`_touched_${key}`] = true;
				}
			});
		} else if (variance > 0) {
			// +ve variance (Excess): REDUCE production.
			let remainingUnits = unitsInt;
			for (const key of [...remainingDateKeys].reverse()) {
				if (remainingUnits <= 0) break;
				const currentDisplayQty = parseFloat(row[key]) || 0;
				if (currentDisplayQty <= 0) continue;

				const availableUnits = Math.floor(currentDisplayQty / constraintUnit);
				const takeUnits = Math.min(remainingUnits, availableUnits);

				if (takeUnits > 0) {
					const targetQty = currentDisplayQty - takeUnits * constraintUnit;
					row[key] = Math.max(0, Math.ceil(targetQty / constraintUnit) * constraintUnit);
					remainingUnits -= takeUnits;
					row[`_touched_${key}`] = true;
				}
			}
			// If still units left, adjust the last available cell anyway (though this shouldn't happen with proper multiples)
			if (remainingUnits > 0) {
				const lastKey = remainingDateKeys[remainingDateKeys.length - 1];
				const currentDisplayQty = parseFloat(row[lastKey]) || 0;
				const adjustment = remainingUnits * constraintUnit;
				row[lastKey] = Math.max(0, Math.ceil((currentDisplayQty - adjustment) / constraintUnit) * constraintUnit);
				row[`_touched_${lastKey}`] = true;
			}
		}

		// Recalculate variance after adjustment
		row.variance = calculateRowVariance(row, totalTarget, originalUnit, row.batch, row.packSize?.quantity);

		// Recalculate finalProduction: Sum fulfilled for past, planned for today/future
		let totalProductionInBase = 0;
		Object.entries(row).forEach(([key, value]) => {
			if (key.startsWith('date_')) {
				const dateStr = key.replace('date_', '');
				const entryDate = moment(dateStr).startOf('day');
				if (entryDate.isBefore(today)) {
					const displayFulfilled =
						parseFloat(row[`_fulfilled_${key}`]) || 0;
					totalProductionInBase += convertToLowerUnit(
						originalUnit,
						displayFulfilled
					);
				} else {
					const displayPlanned =
						value === false ? 0 : parseFloat(value) || 0;
					totalProductionInBase += convertToLowerUnit(
						originalUnit,
						displayPlanned
					);
				}
			}
		});
		const [displayFinalProduction] = getHigherUnitAndQuantity(
			originalUnit,
			totalProductionInBase,
			false
		);
		row.finalProduction = displayFinalProduction;

		newRows[rowIndex] = row;
		setTableData({
			...tableData,
			rows: newRows,
		});
	};

	const handleVarianceWriteOff = (rowIndex) => {
		const newRows = [...tableData?.rows];
		let row = { ...newRows[rowIndex] };

		row.variance = 0;
		row._varianceWrittenOff = true;

		// Recalculate finalProduction: Sum fulfilled for past, planned for today/future
		const today = moment().startOf('day');
		let totalProductionInBase = 0;
		const originalUnit = row.originalUnit;

		Object.entries(row).forEach(([key, value]) => {
			if (key.startsWith('date_')) {
				const dateStr = key.replace('date_', '');
				const entryDate = moment(dateStr).startOf('day');
				if (entryDate.isBefore(today)) {
					const displayFulfilled =
						parseFloat(row[`_fulfilled_${key}`]) || 0;
					totalProductionInBase += convertToLowerUnit(
						originalUnit,
						displayFulfilled
					);
				} else {
					const displayPlanned =
						value === false ? 0 : parseFloat(value) || 0;
					totalProductionInBase += convertToLowerUnit(
						originalUnit,
						displayPlanned
					);
				}
			}
		});
		const [displayFinalProduction] = getHigherUnitAndQuantity(
			originalUnit,
			totalProductionInBase,
			false
		);
		row.finalProduction = displayFinalProduction;

		newRows[rowIndex] = row;
		setTableData({
			...tableData,
			rows: newRows,
		});
	};

	const customRenderer = getTableRenderer(
		mode?.tableRenderKey,
		state?.currentStep
	);

	// Precompute per-date metadata ONCE (instead of calling moment() inside every
	// date cell on every render/scroll). Keyed by the date string (header.id sans
	// "date_"). Cells read this from context — see longTermProductionFourthStepRender.
	const dateMeta = useMemo(() => {
		const today = moment().startOf('day');
		const meta = {};
		(tableData?.headers || []).forEach((h) => {
			const headerId = String(h?.id || '');
			if (!headerId.startsWith('date_')) return;
			const dateStr = headerId.replace('date_', '');
			const isPastDate = moment(dateStr).isBefore(today, 'day');
			const latestPlan = (state?.generatedPlans || [])
				.filter((p) => moment(p.scheduledAt).isSame(moment(dateStr), 'day'))
				.sort((a, b) => (b.id || 0) - (a.id || 0))[0];
			const hasGeneratedPlan =
				latestPlan && latestPlan.status?.toUpperCase() !== 'CANCELLED';
			meta[dateStr] = { isPastDate, hasGeneratedPlan };
		});
		return meta;
	}, [tableData?.headers, state?.generatedPlans]);

	const handleBlur = (rowIndex, headerId) => {
		const newRows = [...tableData?.rows];
		const rowData = newRows[rowIndex];

		const batchVal = parseFloat(rowData?.batch);
		// Pack size is ignored: validity is batch-multiple only (e.g. 11.5 with
		// batch 11.5 is valid and must not error just because pack size is 1).
		const packSizeVal = 0;
		const isStep4 = headerId?.startsWith('date_');
		let hasError = false;

		if (isStep4) {
			Object.entries(rowData).forEach(([key, val]) => {
				if (key.startsWith('date_') && val !== false) {
					const qty = parseFloat(val);
					const isInvalidBatch = isNotMultipleOf(qty, batchVal);
					const isInvalidPack = isNotMultipleOf(qty, packSizeVal);
					if (isInvalidBatch || isInvalidPack) hasError = true;
				}
			});
		} else {
			const qtyVal = parseFloat(rowData[headerId]?.qty || rowData.qty);
			const isInvalidBatch = isNotMultipleOf(qtyVal, batchVal);
			const isInvalidPackSizeMultiple = isNotMultipleOf(qtyVal, packSizeVal);

			hasError = isInvalidBatch || isInvalidPackSizeMultiple;
		}

		newRows[rowIndex] = {
			...newRows[rowIndex],
			[`_touched_${headerId}`]: true,
			// Track error height separately from the frequency editor's increaseHeight.
			hasQtyError: hasError,
			...(isStep4 ? {} : { increaseHeight: hasError }),
		};
		setTableData({
			...tableData,
			rows: newRows,
		});
	};

	const handleCustomDates = (rowIndex, isSelect = true) => {
		const { rows, headers } = tableData;
		let row = rows[rowIndex];
		if (!row) return;
		if (isSelect) {
			const dateUpdates = headers?.reduce((acc, col) => {
				if (col?.id?.startsWith('date_')) {
					const colDate = col.id.replace('date_', '');
					const isPastDate = moment(colDate).isBefore(
						moment(),
						'day'
					);
					acc[col.id] = !(isPastDate || isDateHoliday(colDate));
				}
				return acc;
			}, {});
			row = { ...row, ...dateUpdates };
		} else {
			Object.entries(row).forEach(([key, value]) => {
				if (key.startsWith('date_')) {
					const isPastDate = moment(
						key.replace('date_', '')
					).isBefore(moment(), 'day');
					if (!isPastDate) {
						row[key] = false;
					}
				}
			});
		}
		const newRows = [...tableData?.rows];
		newRows[rowIndex] = row;
		setTableData({ ...tableData, rows: newRows });
	};
	const renderCellData = (rowData, rowIndex, header) => {
		if (customRenderer) {
			return customRenderer({
				rowData,
				rowIndex,
				header,
				context: {
					handleDeleteRow,
					handleChange,
					handleCheckBoxChange,
					handleFrequencyChange,
					handleRepeatDaysChange,
					handleDateQtyChange,
					handleBlur,
					handleVarianceAdjust,
					handleVarianceWriteOff,
					viewMode,
					editMode,
					state,
					handleCustomDates,
					workerAllocations,
					dateMeta,
				},
			});
		}

		// Default render logic
		const getRowClassName = () => {
			if (rowData._isManuallySelected) return '';
			if (rowData._isNotFound) return 'bg-red-100';
			if (rowData._isNewFromDropdown) return 'bg-yellow-100';
			if (rowData._matchType === 'clientVariantId') return 'bg-green-100';
			return '';
		};

		if (header.id === 'Actions') {
			return (
				<div
					className={`text-center text-[12px] p-2 ${getRowClassName()}`}
				>
					<X
						className='w-4 h-4 cursor-pointer text-red-500 hover:text-red-700 transition-colors'
						onClick={(e) => {
							e.stopPropagation();
							handleDeleteRow(rowData);
						}}
						title='Delete row'
					/>
				</div>
			);
		}
		if (header.id === 'S.No')
			return (
				<div className='flex items-center justify-between flex-1 text-[12px]'>
					{' '}
					{rowIndex + 1}{' '}
				</div>
			);
		if (header.id === 'Item Name') {
			// "34 g per pax" - a count item's weight per unit (variant minWeight).
			const itemWeightText = minWeightText(productInfoById?.[getRowItemId(rowData)]);
			if (viewMode) {
				return (
					<div
						className={`w-full text-left text-[12px] flex gap-2 break-words items-center p-1 relative ${getRowClassName()}`}
					>
						<div className='flex flex-col'>
							{rowData['Item Name']}
							{itemWeightText && (
								<span className='text-[10px] text-gray-500'>{itemWeightText}</span>
							)}
						</div>
					</div>
				);
			}

			// For other modes, show edit functionality
			return (
				<div
					className={`w-full text-left text-[12px] flex gap-2 items-center p-1 relative ${getRowClassName()}`}
				>
					{editingRowIndex === rowIndex ? (
						<div
							className='flex items-center gap-2 flex-1'
							onClick={(e) => e.stopPropagation()}
						>
							<SelectPicker
								data={nonTableProducts}
								placeholder='Choose replacement product'
								value={null}
								onChange={(value) => {
									if (value) {
										handleProductSwap(rowData, value);
									}
								}}
								virtualized
								menuStyle={{ zIndex: 99999 }}
								className='flex-1 w-[100px]'
								size='xs'
								cleanable={false}
								searchable={true}
								placement='auto'
								onClose={() => {
									setEditingRowIndex(null);
								}}
							/>
						</div>
					) : (
						<div
							className='flex-1 cursor-pointer relative group flex items-center hover:bg-gray-50 rounded transition-colors'
							onClick={(e) => {
								e.stopPropagation();
								setEditingRowIndex(rowIndex);
							}}
							title='Click to edit product'
						>
							<p
								className='text-left text-[12px] flex-1 break-words pr-6'
								title={rowData['Item Name']}
							>
								{rowData['Item Name']}
								{itemWeightText && (
									<span className='block text-[10px] text-gray-500'>{itemWeightText}</span>
								)}
							</p>
							<Edit
								className='w-3 h-3 absolute right-1 top-1/2 transform -translate-y-1/2 text-blue-500 opacity-60 group-hover:opacity-100 transition-opacity'
								title='Edit product'
							/>
						</div>
					)}
				</div>
			);
		}
		if (header.id === 'SkuCode')
			return (
				<div className='flex items-center  justify-between  text-[12px]'>
					{' '}
					{rowData['SkuCode']}{' '}
				</div>
			);
		if (header.id === 'Total') {
			// Read-only Total for order and dataEntry modes - displays sum
			return (
				<div
					className={`p-1 text-center text-[12px] font-medium ${getRowClassName()}`}
				>
					{getTotal(rowData)}
				</div>
			);
		}

		return (
			<div className={`p-2 ${getRowClassName()}`}>
				{renderStoreCell(rowData, rowIndex, header)}
			</div>
		);
	};

	const handleGeneratePlan = async (header, existingPlan) => {
		const date = header.id?.replace('date_', '');

		if (existingPlan) {
			const req = {
				params: {
					planId: existingPlan.id,
				},
			};
			const loadingId = notificationServices.generateNotification({
				type: 'loading',
				message: 'Opening production plan…',
			});
			try {
				const response = await productionPlanServices.getPlanDetails(
					req
				);
				if (response?.statusCode === 200) {
					handleOpenProductionPlanModal({
						data: response?.data,
						date: existingPlan.scheduledAt,
						storeId: state?.kitchen,
						productionPlanId: existingPlan?.id,
						hideButtons: true,
						nonEditable: true,
					});
				} else {
					throw new Error(
						response?.errorMessage || 'Failed to open production plan.'
					);
				}
			} catch (error) {
				notificationServices.generateNotification({
					type: 'error',
					message:
						error ||
						'Something went wrong while opening the production plan.',
				});
			} finally {
				notificationServices.closeNotification(loadingId);
			}
			return;
		}

		const hasQtyForDate = tableData?.rows?.some((row) => {
			const val = row[header?.id];
			return (
				val !== false &&
				val !== null &&
				val !== undefined &&
				val !== '' &&
				parseFloat(val) > 0
			);
		});
		if (!hasQtyForDate) {
			notificationServices.generateNotification({
				type: 'warning',
				message:
					'Please add Production Qty for this date to raise the production plan.',
			});
			return;
		}

		const initiatePlan = async (distList = []) => {
			const loadingId = notificationServices.generateNotification({
				type: 'loading',
				message: 'Opening production plan…',
			});
			try {
				const response = await productionPlanServices.initiateLppPlan({
					data: {
						date: date,
						storeId: state?.kitchen,
						lppId: state?.planId,
						...(Array.isArray(distList)
							? { shiftDistribution: distList }
							: {}),
					},
				});
				if (response?.statusCode === 200) {
					handleOpenProductionPlanModal({
						data: response?.data,
						date,
						storeId: state?.kitchen,
						shiftDistribution: distList,
					});
				} else {
					throw new Error(
						response?.errorMessage || 'Failed to generate production plan.'
					);
				}
			} catch (error) {
				console.error('Error generating plan:', error);
				notificationServices.generateNotification({
					type: 'error',
					message:
						error ||
						'Something went wrong while generating the production plan.',
				});
			} finally {
				notificationServices.closeNotification(loadingId);
			}
		};

		if (state?.shift !== -999) {
			await initiatePlan();
		} else {
			// Show shift distribution modal
			const handleSaveDistribution = async (distribution) => {
				const list = Object.entries(distribution).map(
					([key, value]) => ({
						shiftId: parseFloat(key),
						percentage: value,
					})
				);
				reduxDispatch(modalActions.toggleCustomModal(false));
				await initiatePlan(list);
			};

			const distributionRef = {
				current: { distribution: {}, isValid: false },
			};

			reduxDispatch(
				modalActions.toggleCustomModal(true, {
					header: 'Distribute Work Across Shifts',
					subtitle:
						'Allocate work percentage to each shift. Total must equal 100%',
					bodyContent: (
						<ShiftDistributionContent
							shifts={lists?.shiftList || []}
							distributionRef={distributionRef}
						/>
					),
					actionButtons: (
						<div className='flex justify-end space-x-3 w-full'>
							<ReactiveSaveButton
								distributionRef={distributionRef}
								onSave={handleSaveDistribution}
							/>
						</div>
					),
					width: '36rem',
					height: '28rem',
					bodyClass: 'px-6 py-2',
					onClose: () =>
						reduxDispatch(modalActions.toggleCustomModal(false)),
				})
			);
		}
	};

	const handleWorkerValueChange = (date, grade, value) => {
		if (grade === 'isHoliday') {
			const currentAllocation = workerAllocations[date] || {
				gradeA: '',
				gradeB: '',
				gradeC: '',
				isHoliday: false,
			};
			const currentIsHoliday = !!currentAllocation.isHoliday;
			const newIsHoliday = !currentIsHoliday;

			setWorkerAllocations((prev) => ({
				...prev,
				[date]: {
					...(prev[date] || {
						gradeA: '',
						gradeB: '',
						gradeC: '',
						isHoliday: false,
					}),
					isHoliday: newIsHoliday,
				},
			}));

			const dateKey = `date_${date}`;

			if (editMode) {
				const prevKey = `_prev_${dateKey}`;
				const today = moment().startOf('day');

				const newRows = tableData?.rows?.map((row) => {
					let updatedRow = { ...row };
					if (row[dateKey] !== undefined) {
						if (newIsHoliday) {
							// Backup current value if it's not already false
							if (row[dateKey] !== false) {
								updatedRow[prevKey] = row[dateKey];
							}
							updatedRow[dateKey] = false;
						} else {
							// Restore from backup
							updatedRow[dateKey] =
								row[prevKey] !== undefined ? row[prevKey] : 0;
						}

						// Recalculate variance and final production for the row
						if (updatedRow._totalTargetQuantity !== undefined) {
							updatedRow.variance = calculateRowVariance(
								updatedRow,
								updatedRow._totalTargetQuantity,
								updatedRow.originalUnit
							);
						}

						let totalProduction = 0;
						Object.entries(updatedRow).forEach(([key, val]) => {
							if (key.startsWith('date_')) {
								const dStr = key.replace('date_', '');
								const entryDate = moment(dStr).startOf('day');

								if (entryDate.isBefore(today)) {
									const displayFulfilled =
										parseFloat(
											updatedRow[`_fulfilled_${key}`]
										) || 0;
									totalProduction += displayFulfilled;
								} else {
									const displayPlanned =
										val === false
											? 0
											: parseFloat(val) || 0;
									totalProduction += displayPlanned;
								}
							}
						});
						updatedRow.finalProduction = totalProduction;
						return updatedRow;
					}
					return row;
				});

				if (newRows) {
					setTableData((prev) => ({ ...prev, rows: newRows }));
				}
			} else {
				// When marking as holiday (Y) in non-edit mode, disable that date in all table rows
				if (newIsHoliday) {
					const newRows = tableData?.rows?.map((row) => {
						if (row[dateKey] !== undefined) {
							return { ...row, [dateKey]: false };
						}
						return row;
					});
					if (newRows) {
						setTableData((prev) => ({ ...prev, rows: newRows }));
					}
				}
			}
		} else {
			setWorkerAllocations((prev) => ({
				...prev,
				[date]: {
					...(prev[date] || {
						gradeA: '',
						gradeB: '',
						gradeC: '',
						isHoliday: false,
					}),
					[grade]: value,
				},
			}));
		}
	};

	const renderWorkerCell = (header, type) => {
		const dateStr = header.id.replace('date_', '');
		const allocation = workerAllocations[dateStr] || {
			gradeA: '',
			gradeB: '',
			gradeC: '',
			isHoliday: false,
		};
		const isHoliday =
			allocation.isHoliday !== undefined && allocation.isHoliday;
		const isPastDate = moment(dateStr).isBefore(moment(), 'day');

		if (type === 'holiday_day') {
			if (isPastDate) {
				if (isHoliday) {
					return (
						<div className='px-3 py-1 rounded font-bold text-sm bg-green-100 text-green-700'>
							<Check size={14} />
						</div>
					);
				}
				return (
					<div className='px-3 py-1 rounded font-bold text-sm bg-gray-200 text-gray-800'>
						N
					</div>
				);
			}
			return (
				<button
					onClick={() =>
						handleWorkerValueChange(dateStr, 'isHoliday')
					}
					className={`px-3 py-1 rounded font-bold text-sm transition-colors ${isHoliday
						? 'bg-green-100 text-green-700 hover:bg-green-200'
						: 'w-5 h-5 flex items-center justify-center rounded border border-gray-400 transition-colors duration-200'
						}`}
				>
					{isHoliday ? <Check size={14} /> : ''}
				</button>
			);
		}
		if (isPastDate || isHoliday) {
			return (
				<div className='w-[30px] text-center border border-gray-200 rounded px-1 py-0.5 text-xs bg-gray-200 text-gray-800 transition-colors flex items-center justify-center'>
					<X size={12} />
				</div>
			);
		}
		return (
			<input
				type='text'
				value={allocation[type] || ''}
				onChange={(e) => {
					const val = e.target.value;
					if (val === '' || /^\d+$/.test(val)) {
						handleWorkerValueChange(dateStr, type, val);
					}
				}}
				className='w-[40px] text-center border border-gray-300 rounded px-1 py-0.5 text-xs focus:border-blue-500 outline-none hover:bg-blue-50 focus:bg-white transition-colors'
				placeholder='0'
			/>
		);
	};

	const workerFooterRows =
		state?.currentStep === 3 || (state?.currentStep === 4 && editMode)
			? (() => {
				const allocationValues = Object.values(workerAllocations);
				const holidayCount = allocationValues.filter(
					(a) => a.isHoliday === true
				).length;
				const totalGradeA = allocationValues.reduce(
					(sum, a) => sum + (parseInt(a.gradeA) || 0),
					0
				);
				const totalGradeB = allocationValues.reduce(
					(sum, a) => sum + (parseInt(a.gradeB) || 0),
					0
				);
				const totalGradeC = allocationValues.reduce(
					(sum, a) => sum + (parseInt(a.gradeC) || 0),
					0
				);

				return [
					{
						label: 'Holiday',
						icon: <CalendarDays size={16} />,
						rowClass: 'bg-slate-100 border-slate-200',
						cellHoverClass: 'hover:bg-slate-200',
						renderCellStatus: (header) =>
							renderWorkerCell(header, 'holiday_day'),
						collapsedSummary: `${holidayCount} holidays`,
						isUnsaved: false,
					},
					{
						label: 'Worker Grade A',
						icon: <UserCheck size={16} />,
						rowClass: 'bg-blue-50 border-blue-100',
						cellHoverClass: 'hover:bg-blue-100',
						renderCellStatus: (header) =>
							renderWorkerCell(header, 'gradeA'),
						collapsedSummary: `Total: ${totalGradeA}`,
						isUnsaved: false,
					},
					{
						label: 'Worker Grade B',
						icon: <Users size={16} />,
						rowClass: 'bg-indigo-50 border-indigo-100',
						cellHoverClass: 'hover:bg-indigo-100',
						renderCellStatus: (header) =>
							renderWorkerCell(header, 'gradeB'),
						collapsedSummary: `Total: ${totalGradeB}`,
						isUnsaved: false,
					},
					{
						label: 'Worker Grade C',
						icon: <UserPlus size={16} />,
						rowClass: 'bg-cyan-50 border-cyan-100',
						cellHoverClass: 'hover:bg-cyan-100',
						renderCellStatus: (header) =>
							renderWorkerCell(header, 'gradeC'),
						collapsedSummary: `Total: ${totalGradeC}`,
						isUnsaved: false,
					},
				];
			})()
			: [];

	const handlePriorityToggle = (headerId, currentPriority) => {
		changeQtyRef.current = false;
		const newPriority = !currentPriority;
		const updatedOrderInfo = {
			...(state.orderInfo || {}),
			[headerId]: {
				...(state.orderInfo?.[headerId] || {}),
				isPriority: newPriority,
			},
		};
		dispatch({
			type: 'SET_STATE',
			payload: { orderInfo: updatedOrderInfo },
		});

		const newHeaders = tableData.headers.map((h) => {
			if (h.id === headerId) {
				return { ...h, priority: newPriority };
			}
			return h;
		});
		setTableData({ ...tableData, headers: newHeaders });
	};

	const renderHeaderContent = (header) => {
		if (
			modeName === 'order' &&
			header?.notPunched &&
			!['S.No', 'Item Name', 'SkuCode', 'Total', 'Actions'].includes(
				header.id
			)
		) {
			return (
				<div
					className='mt-1 flex items-center justify-center gap-1 text-red-500 text-[9px] font-medium whitespace-nowrap cursor-default'
					title='This store has not placed any order'
				>
					<Info size={10} />
					<span>Order not punched</span>
				</div>
			);
		}
		if (
			modeName === 'orderWise' &&
			![
				'S.No',
				'Item Name',
				'SkuCode',
				'Total',
				'Actions',
				'S.No',
			].includes(header.id)
		) {
			const isPriority =
				state.orderInfo?.[header.id]?.isPriority ??
				header.priority ??
				false;
			return (
				<>
					<div>
						{header?.orderId && (
							<div className='text-xs text-gray-500 font-medium'>
								#{header.orderId} {header?.jsonData?.parentDisplayPoId ? `(${header?.jsonData?.parentDisplayPoId})` : ''}
							</div>
						)}
					</div>
					<div
						className='flex items-center justify-center mt-1 cursor-pointer'
						onClick={(e) => {
							e.stopPropagation();
							handlePriorityToggle(header.id, isPriority);
						}}
					>
						<span className='text-xs mr-2 font-medium text-gray-600'>
							Priority
						</span>
						<div
							className={`relative inline-flex h-4 w-8 items-center rounded-full transition-colors ${isPriority ? 'bg-red-200' : 'bg-gray-300'
								}`}
						>
							<span
								className={`${isPriority
									? 'translate-x-[18px] bg-red-500'
									: 'translate-x-1 bg-gray-600'
									} inline-block h-2.5 w-2.5 transform rounded-full transition duration-200 ease-in-out`}
							/>
						</div>
					</div>
				</>
			);
		}
		return null;
	};

	const handleSortColumn = (name, type) => {
		setSortColumn(name);
		setSortType(type);
	};

	return (
		<div className='flex flex-col gap-2 h-full overflow-hidden'>
			{/* Fixed Header Section */}
			<div className='flex-shrink-0'>
				{(!mode?.hideComp ||
					!mode?.hideComp.includes('multiPOGeneralDetails')) && (
						<MultiPOGeneralDetails
							fields={fields}
							layoutConfig={mode?.layoutConfig}
							editMode={editMode}
							lists={lists}
							viewMode={viewMode}
							onFieldChange={handleHeaderValueChange}
							modeName={modeName}
						/>
					)}
				{(!mode?.hideComp ||
					!mode?.hideComp.includes('functional')) && (
						<Functional
							stores={storeList.current}
							functionality={mode?.functionality}
							mode={{ ...mode, modeName }}
							currentStep={state?.currentStep}
							setConfirmationModal={setConfirmationModal}
							editMode={editMode}
						/>
					)}
				{(!mode?.hideComp || !mode?.hideComp.includes('legend')) && (
					<Legend modeName={modeName} />
				)}
			</div>
			{/* Scrollable Table Section */}
			<div className='flex-1 min-h-0 flex flex-col overflow-hidden'>
				<PoTableMultiple
					fitParent
					tableData={tableData}
					mode={modeName}
					showHeader={mode?.showHeader}
					renderCellData={renderCellData}
					handleSortColumn={handleSortColumn}
					sortColumn={sortColumn}
					sortType={sortType}
					renderHeaderContent={renderHeaderContent}
					headerHeight={
						modeName === 'orderWise'
							? 100
							: modeName === 'order' && editMode
								? 80
								: null
					}
					fixedColumns={
						mode?.tableConfig?.fixedColumns || [
							'S.No',
							'Item Name',
							'SkuCode',
						]
					}
					columnWidth={mode?.tableConfig?.columnWidth}
					generatedPlans={state?.generatedPlans}
					virtulization={state?.currentStep !== 3}
					isUnsaved={!changeQtyRef.current}
					emptyMessage={mode?.emptyMessage}
					showGenerateRow={!!(mode?.footerRow && editMode)}
					onGeneratePlan={handleGeneratePlan}
					generateRows={workerFooterRows}
					footerPlacement='top'
					showGenerateRowPlacement='bottom'
					showGroupedHeaders={mode?.tableConfig?.showGroupedHeaders}
				// renderHeaderContent={(header) => {
				// 	const isStore = storeList.current?.some(
				// 		(s) => s.id === header.id
				// 	);
				// 	if (isStore || (orderwise && header?.orderId)) {
				// 		const isPriority =
				// 			state?.orderInfo?.[header.id]?.isPriority ||
				// 			false;
				// 		return (
				// 			<div className='flex flex-col items-center justify-center gap-1'>
				// 				{orderwise && header?.orderId && (
				// 					<div className='text-[10px] text-gray-500 font-medium'>
				// 						#{header.orderId}
				// 					</div>
				// 				)}
				// 				<div
				// 					className='flex items-center justify-center mt-1 cursor-pointer'
				// 					onClick={(e) => {
				// 						e.stopPropagation();
				// 						const newOrderInfo = {
				// 							...(state?.orderInfo || {}),
				// 							[header.id]: {
				// 								...(state?.orderInfo?.[
				// 									header.id
				// 								] || {}),
				// 								isPriority: !isPriority,
				// 							},
				// 						};
				// 						dispatch({
				// 							type: 'SET_STATE',
				// 							payload: {
				// 								orderInfo: newOrderInfo,
				// 							},
				// 						});
				// 					}}
				// 				>
				// 					<div
				// 						className={`relative inline-flex h-4 w-8 items-center rounded-full transition-colors ${
				// 							isPriority
				// 								? 'bg-red-200'
				// 								: 'bg-gray-300'
				// 						}`}
				// 					>
				// 						<span
				// 							className={`${
				// 								isPriority
				// 									? 'translate-x-[18px] bg-red-500'
				// 									: 'translate-x-1 bg-gray-600'
				// 							} inline-block h-2.5 w-2.5 transform rounded-full transition duration-200 ease-in-out`}
				// 						/>
				// 					</div>
				// 				</div>
				// 			</div>
				// 		);
				// 	}
				// 	return null;
				// }}
				/>
			</div>
		</div>
	);
}

export default MultiPoContent;
